import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Button, Card, CardContent, Grid } from '@mui/material';
import Delete from '@mui/icons-material/Delete';

import ApprovalAutomationsSection from '../../waterAuthoritySubmissions/components/ApprovalAutomationsSection';
import BackflowJobGroupInfoHeader from '../components/BackflowJobGroupInfoHeader';
import CreateBackflowJobItemModal from '../components/CreateBackflowJobItemModal';
import DataTable from '../../../sharedModules/dataTable';
import DeleteConfirmationModal from '../../../sharedModules/layout/DeleteConfirmationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import ShakingAlert, { ShakingAlertHandle } from '../../../sharedModules/layout/ShakingAlert';
import StatusCell from '../../../sharedModules/layout/StatusCell';
import WaterAuthorityUpdatingFields from '../../waterAuthorityConfig/components/WaterAuthorityUpdatingFields';

import { minimumLoading } from '../../../utils/minimumLoading';

import { BackflowJobGroup, BackflowJobItem } from '../../model';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { DeleteConfirmationModalContext } from '../../../sharedModules/contexts/DeleteConfirmationModalContext';

import axios from '../../../sharedModules/utils/axios.utils';

const BackflowJobGroupDetailPage: React.FC = () => {
  const { backflowJobGroupId, formAction } = useParams<{ backflowJobGroupId: string; formAction?: string }>();
  const history = useHistory();

  const { user } = useContext(AuthenticationContext);
  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [backflowJobGroup, setBackflowJobGroup] = useState<BackflowJobGroup>({});
  const [backflowJobItems, setBackflowJobItems] = useState<BackflowJobItem[]>();
  const [createJobItemModalOpen, setCreateJobItemModalOpen] = useState<boolean>(false);
  const [jobErrorMessage, setJobErrorMessage] = useState<string>('');
  const [itemsErrorMessage, setItemsErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const shakingAlertRef = useRef<ShakingAlertHandle>(null);

  const handleDelete = (backflowJobGroup) => {
    setModelLabel('Device');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(backflowJobGroup);
    setDeleteURL('backflows/backflow_job_items/');
  };

  const backflowJobItemsColumns = [
    {
      name: 'Asset No',
      selector: 'asset_no',
      sortable: true,
    },
    {
      name: 'Location',
      selector: 'location',
      sortable: true,
    },
    {
      name: 'Model No',
      selector: 'model_no',
      sortable: true,
    },
    {
      name: 'Size',
      selector: 'backflow_size',
      sortable: true,
      cell: (row: any) => (row.backflow_size ? `${row.backflow_size} inch` : 'Unknown'),
    },
    {
      name: 'Result',
      selector: 'pass_or_fail',
      sortable: true,
      cell: (row: any) => <StatusCell status={row.pass_or_fail} />,
    },
    {
      name: 'Logging Tech',
      selector: 'display_emp_id',
      sortable: true,
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      cell: (row) => <StatusCell status={row.status} />,
    },
    {
      name: 'Actions',
      selector: 'actions',
      grow: 0.5,
      cell: (row) => (
        <Button variant="contained" color="warning" onClick={() => handleDelete(row)} disabled={!row.allow_delete}>
          <Delete />
        </Button>
      ),
    },
  ];

  const fetchBackflowJobGroup = async () => {
    const response: any = await axios.get(`/backflows/backflow_job_groups/${backflowJobGroupId}`);
    setBackflowJobGroup(response.data.result);
    setJobErrorMessage('');
  };

  const fetchBackflowJobItems = async () => {
    try {
      const response: any = await axios.get('/backflows/backflow_job_items', {
        params: { 'ransack[backflow_job_group_id_eq]': backflowJobGroupId },
      });
      setBackflowJobItems(response.data.result);
      setItemsErrorMessage('');
    } catch (error) {
      setItemsErrorMessage('Something went wrong with the Items');
    }
  };

  useEffect(() => {
    if (backflowJobGroupId && user) {
      void minimumLoading(async () => {
        await Promise.all([fetchBackflowJobGroup(), fetchBackflowJobItems()]);
      }, setLoading);
    }
  }, [backflowJobGroupId, user]);

  const showWaterAuthorityWarning = () => {
    if (formAction !== 'approve') return false;
    if (backflowJobGroup?.water_authority_name === 'Unknown') return true;
    if (backflowJobGroup?.authority_provided_values_complete === 'missing') return true;
    return false;
  };

  const handleRowClicked = (row: any) => {
    if (showWaterAuthorityWarning()) {
      shakingAlertRef.current?.shake();
      return;
    }

    if (formAction) {
      history.push(`/backflow_tests/backflow_form/${row.id}/${formAction}`);
    } else {
      history.push(`/backflow_tests/backflow_form/${row.id}`);
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <CreateBackflowJobItemModal
        backflowJobGroup={backflowJobGroup}
        fetchBackflowJobItems={fetchBackflowJobItems}
        fetchBackflowJobGroup={fetchBackflowJobGroup}
        createJobItemModalOpen={createJobItemModalOpen}
        setCreateJobItemModalOpen={setCreateJobItemModalOpen}
      />
      <DeleteConfirmationModal afterDelete={fetchBackflowJobItems} />
      <Grid container spacing={2} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader
            mainTitle="Backflow Testing"
            subTitle={`${backflowJobGroup.company_name} (Order: ${backflowJobGroup.order_no})`}
            backButtonLabel={formAction === 'approve' ? 'Approvals Queue' : 'All Backflow Tests'}
            backButtonLink={formAction === 'approve' ? '/backflow_tests/approvals' : '/backflow_tests/search'}
          />
        </Grid>
        <Grid item xs={12} style={{ marginTop: 10 }}>
          <BackflowJobGroupInfoHeader backflowJobGroup={backflowJobGroup} />
        </Grid>
        {showWaterAuthorityWarning() && (
          <>
            <Grid item xs={12} style={{ marginBottom: 10 }}>
              <ShakingAlert ref={shakingAlertRef} message="Water Authority Info Missing." severity="warning" />
            </Grid>
            <Grid item xs={6} style={{ marginBottom: 10 }}>
              <WaterAuthorityUpdatingFields
                backflowJobGroup={backflowJobGroup}
                fetchBackflowJobGroup={fetchBackflowJobGroup}
              />
            </Grid>
          </>
        )}
        <ApprovalAutomationsSection backflowJobGroupId={backflowJobGroupId ?? ''} />
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Card>
            <CardContent>
              <DataTable
                title="Backflow Test Assets"
                columns={backflowJobItemsColumns}
                data={backflowJobItems || []}
                onRowClicked={handleRowClicked}
                dense
                highlightOnHover
                pointerOnHover={!showWaterAuthorityWarning()}
                striped
                responsive
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={jobErrorMessage} />
          <ErrorMessage message={itemsErrorMessage} />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={() => setCreateJobItemModalOpen(true)}>
            Add Asset to Backflow Test
          </Button>
        </Grid>
        {formAction === 'approve' && (
          <Grid item xs={6}>
            <WaterAuthorityUpdatingFields
              backflowJobGroup={backflowJobGroup}
              fetchBackflowJobGroup={fetchBackflowJobGroup}
            />
          </Grid>
        )}
      </Grid>
    </LoadingWrapper>
  );
};

export default BackflowJobGroupDetailPage;
